import {
  PresentationPageFragment,
  useGetPageImageTokensQuery,
} from '@sketch/gql-types'
import { usePresentationManifest } from './usePresentationManifest'
import { useEffect, useMemo, useRef } from 'react'
import { usePreview } from '../../shares/views/PreviewView/PreviewContext'
import produce from 'immer'

type ImageTokens = Record<string, string>

type Page = {
  uuid: string | null
  presentationFile: { imageToken: string } | null
}

function toImageTokens(pages: readonly Page[]): ImageTokens {
  const imageTokens: ImageTokens = {}

  for (const page of pages) {
    imageTokens[page.uuid ?? ''] = page.presentationFile?.imageToken ?? ''
  }

  return imageTokens
}

/**
 * The manifest and the image tokens describe the same version of the same document, and the
 * renderer resolves its asset urls once, when it is created — so it must never be handed one
 * without the other. This pairs the two fetches behind a single `loading`, and reports
 * `tokensPerPage: null` until the tokens for the current version have actually arrived.
 */
export const usePrototypeManifest = (
  shareIdentifier: string,
  /**
   * The version the `page` below came from — not the version the route is on.
   * Image tokens are minted per page row, and every version has its own row
   * for the same page uuid, so a token fetched for a different version is
   * rejected with a 403 even though it looks perfectly valid.
   */
  versionShortId: string | null,
  page?: PresentationPageFragment | null
) => {
  /**
   * In a preview the viewer is anonymous and there is no real share to query — `useVersioning`
   * hands out a mock one with an empty identifier. The pages, and their tokens, come with the
   * preview payload instead.
   */
  const previewContext = usePreview()

  const manifestUrl = page?.presentationFile?.manifestUrl ?? null

  const manifestData = usePresentationManifest(manifestUrl)

  /**
   * `useFetchJSON` reports `loading` from an effect, so on the render where the
   * url changes its result still describes the previous page — and when the url
   * goes away it keeps reporting the old one indefinitely. Remember which url
   * it actually started fetching, so the tokens below are never paired with a
   * manifest for a different version.
   */
  const startedUrl = useRef(manifestUrl)

  if (manifestData.status === 'loading') {
    startedUrl.current = manifestUrl
  }

  const resultIsForCurrentUrl = startedUrl.current === manifestUrl
  const manifestReady =
    resultIsForCurrentUrl && manifestData.status === 'complete'
  const manifestFailed =
    resultIsForCurrentUrl && manifestData.status === 'error'

  const presentationAssets = useGetPageImageTokensQuery({
    variables: {
      shareIdentifier,
      versionShortId: versionShortId ?? '',
    },
    skip: !!previewContext || !versionShortId || !manifestReady,
    // so that `loading` also covers the follow-up pages fetched below
    notifyOnNetworkStatusChange: true,
  })

  const { fetchMore } = presentationAssets
  const pages = presentationAssets.data?.share?.version?.document?.pages
  const nextAfter = pages?.meta?.after ?? null

  /**
   * Tokens are scoped per document page and the query is paginated, so a document with more
   * pages than fit in one response would otherwise be missing tokens for the rest — and every
   * image on those pages would fail to resolve. Keep asking until there is no cursor left.
   */
  const requestedAfter = useRef<string | null>(null)

  /**
   * Cursors are only meaningful within one query. Forget them when the query
   * changes, or a new version whose first response happens to repeat the last
   * cursor would look like a page we had already asked for, and its remaining
   * pages would never be fetched.
   */
  const queryIdentity = `${shareIdentifier}:${versionShortId ?? ''}`
  const requestedFor = useRef(queryIdentity)

  if (requestedFor.current !== queryIdentity) {
    requestedFor.current = queryIdentity
    requestedAfter.current = null
  }

  /**
   * A page we have been given a cursor for but have not asked for yet. Asking
   * is what clears it: each cursor is requested at most once, so this cannot
   * stay true if the server keeps handing back the same cursor.
   */
  const hasUnfetchedPage = !!nextAfter && requestedAfter.current !== nextAfter

  useEffect(() => {
    if (!nextAfter || requestedAfter.current === nextAfter) return

    requestedAfter.current = nextAfter

    fetchMore({
      variables: { after: nextAfter },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.share?.version?.document?.pages) return previous

        return produce(fetchMoreResult, draft => {
          const pages = draft.share?.version?.document?.pages

          if (!pages) return

          pages.entries = [
            ...(previous?.share?.version?.document?.pages?.entries ?? []),
            ...(pages.entries ?? []),
          ]
        })
      },
    }).catch(() => {
      // Surfaced through the query's own `error`; swallowed here only so a
      // failed page does not become an unhandled rejection.
    })
  }, [nextAfter, fetchMore])

  const tokensPerPage = useMemo(() => {
    if (previewContext) {
      return toImageTokens(previewContext.preview.pages.entries)
    }

    const entries = pages?.entries

    // `null` rather than `{}`: callers must be able to tell "no tokens yet"
    // from "this version has no tokens", and only the first is safe to wait on.
    if (!entries) return null

    return toImageTokens(entries)
  }, [previewContext, pages])

  const loading =
    (!manifestReady && !manifestFailed) ||
    (!previewContext && (presentationAssets.loading || hasUnfetchedPage))

  const error =
    (manifestFailed ? manifestData.error : undefined) ||
    presentationAssets.error

  const manifest = manifestReady ? manifestData.data : null

  return {
    loading,
    error,
    manifest,
    tokensPerPage,
  }
}
